import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CameraOff, CheckCircle2, Loader2, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { QrScanner } from "./QrScanner";

const RESULT = {
  ok: ["Valid — let them in", "border-[#00d436] bg-[#00d436]/10 text-lime", CheckCircle2],
  already_used: ["Already used", "border-red-400 bg-red-500/10 text-red-300", XCircle],
  not_found: ["Code not found", "border-red-400 bg-red-500/10 text-red-300", XCircle],
  wrong_show: ["Ticket is for a different show", "border-amber-300 bg-amber-400/10 text-amber-200", XCircle],
};

export const CheckinPanel = () => {
  const [concerts, setConcerts] = useState([]);
  const [stats, setStats] = useState({});
  const [concertId, setConcertId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const lastScan = useRef({ code: "", at: 0 });

  const loadStats = useCallback(() =>
    api.checkinStats().then((s) => setStats(Object.fromEntries(s.map((x) => [x.concert_id, x])))).catch(() => {}), []);
  useEffect(() => {
    api.concerts().then(setConcerts).catch(() => {});
    loadStats();
  }, [loadStats]);

  const check = useCallback(async (raw) => {
    const value = raw.trim();
    if (!value) return;
    setBusy(true);
    try {
      const r = await api.checkin(value, concertId || null);
      setResult({ ...r, code: r.ticket?.code || r.code || value.toUpperCase() });
      setCode("");
      loadStats();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [concertId, loadStats]);

  const onScan = useCallback((text) => {
    const now = Date.now();
    if (text === lastScan.current.code && now - lastScan.current.at < 4000) return;
    lastScan.current = { code: text, at: now };
    check(text);
  }, [check]);

  const undo = async () => {
    try {
      await api.undoCheckin(result.code);
      toast.success("Check-in undone");
      setResult(null);
      loadStats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [label, tone, Icon] = result ? RESULT[result.result] : [];
  const s = concertId ? stats[concertId] : null;

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr]" data-testid="checkin-panel">
      <div className="space-y-5">
        <div>
          <label className="block font-cond uppercase tracking-widest text-xs text-slate-400 mb-2">Show at the door</label>
          <select className="field" value={concertId} onChange={(e) => setConcertId(e.target.value)} data-testid="checkin-concert-select">
            <option value="">Any show</option>
            {concerts.map((c) => <option key={c.id} value={c.id}>{c.date} — {c.title}</option>)}
          </select>
          {s && <p className="text-sm text-slate-400 mt-2" data-testid="checkin-stats">{s.used} of {s.issued} tickets checked in</p>}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); check(code); }} className="flex gap-3">
          <input className="field font-mono-ui uppercase" placeholder="00S-XXXXXXXX" value={code} onChange={(e) => setCode(e.target.value)}
            data-testid="checkin-code-input" autoFocus />
          <button type="submit" disabled={busy || !code.trim()} data-testid="checkin-submit-button"
            className="btn-lime disabled:!opacity-60 rounded-full px-6 font-cond uppercase font-bold tracking-widest shrink-0 flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Check in
          </button>
        </form>
        <button onClick={() => setScanning((v) => !v)} data-testid="checkin-scan-toggle"
          className="btn-line rounded-full px-6 py-3 font-cond uppercase tracking-widest inline-flex items-center gap-2">
          {scanning ? <CameraOff className="w-4 h-4" /> : <Camera className="w-4 h-4" />} {scanning ? "Stop camera" : "Scan QR with camera"}
        </button>
        {scanning && <QrScanner onScan={onScan} />}
      </div>
      <div>
        {result ? (
          <div className={`rounded-2xl border-2 p-8 ${tone}`} data-testid={`checkin-result-${result.result}`}>
            <Icon className="w-12 h-12" />
            <p className="font-cond uppercase font-black text-4xl mt-4">{label}</p>
            <p className="font-mono-ui mt-2 text-slate-200" data-testid="checkin-result-code">{result.code}</p>
            {result.ticket && (
              <div className="text-sm text-slate-300 mt-4 space-y-1">
                <p>{result.ticket.concert_title} · {result.ticket.concert_date}</p>
                <p>Buyer: {result.ticket.email}</p>
                {result.ticket.used_at && <p>Checked in {new Date(result.ticket.used_at).toLocaleString()} by {result.ticket.used_by}</p>}
              </div>
            )}
            {result.result === "ok" && (
              <button onClick={undo} data-testid="checkin-undo-button" className="btn-line rounded-full px-5 py-2 mt-6 text-sm font-cond uppercase tracking-widest inline-flex items-center gap-2 text-slate-200">
                <Undo2 className="w-4 h-4" /> Undo
              </button>
            )}
          </div>
        ) : (
          <div className="card-surface rounded-2xl p-8 text-slate-400 text-sm" data-testid="checkin-idle">
            Type a ticket code or scan the QR code from the buyer's email. Each code can only be used once.
          </div>
        )}
      </div>
    </div>
  );
};
